import { useCallback, useMemo, useContext, useEffect } from 'react';
import { AppContext } from '../../context/Provider';
import SelectDropdown from '../SelectDropdown';
import { SET_SELECTED_FILTERS } from '../../context/actions';
import {
	APPLICATIONS,
	APPLICATION_OPTIONS,
	DEFAULT_FILTERS,
	LABELS,
	SOLUTIONS,
} from '../../config/data';
import {
	APPLICATION_SELECTED_EVENT,
	OPTIMIZATION_SELECTED_EVENT,
	SENSOR_SELECTED_EVENT,
} from '../../config/analytics.telemetry';
import { sendUserInteractionsToTelemetryFor } from '../../utility/analytics.telemetry';
import './style.css';

/**
 * Renders the 3 solution filters for Application, SensorType and Optimization
 * @returns {React.ReactComponentElement}
 */
const SolutionFilters = () => {
	const classes = ['adi-isp__solution-filters'];
	const { globalState, dispatch } = useContext(AppContext);
	const { selectedFilters } = globalState;
	const { application, sensorType, optimization, solutionId } = selectedFilters;
	const { filters, applicationFilter, sensorTypeFilter, optimizationFilter } = LABELS;

	const selectedApplication = useMemo(
		// Default to first APPLICATION in case of invalid 'application'
		() => APPLICATIONS.find(filter => filter.name === application) ?? APPLICATIONS[0],
		[application],
	);
	const sensorTypeOptions = useMemo(
		() => selectedApplication.sensorTypes.map(sensorType => sensorType.name),
		[selectedApplication],
	);
	const selectedSensorType = useMemo(
		() =>
			selectedApplication.sensorTypes.find(filter => filter.name === sensorType) ??
			selectedApplication.sensorTypes[0], // In case of invalid 'sensorType'
		[selectedApplication, sensorType],
	);
	const optimizationOptions = useMemo(
		() => selectedSensorType.optimizations.map(optimization => optimization.name),
		[selectedSensorType],
	);
	const isApplicationInvalid = useMemo(
		() => !APPLICATION_OPTIONS.includes(application),
		[application],
	);
	const isSensorTypeInvalid = useMemo(
		() => !sensorTypeOptions.includes(sensorType),
		[sensorTypeOptions, sensorType],
	);
	const isOptimizationInvalid = useMemo(
		() => !optimizationOptions.includes(optimization),
		[optimizationOptions, optimization],
	);
	const isSolutionIdInvalid = useMemo(
		() => !SOLUTIONS.some(solution => solution.solutionId === solutionId),
		[solutionId],
	);
	const isAnyParamInvalid =
		isApplicationInvalid || isSensorTypeInvalid || isOptimizationInvalid || isSolutionIdInvalid;

	/**
	 * Selects first values for 'sensorType' and 'optimization', updates
	 * 'selectedFilters' global state and url params
	 * @param {string} newValue Updated value of 'application' filter dropdown
	 */
	const onApplicationFilterChange = useCallback(
		newValue => {
			const selectedApplication = APPLICATIONS.find(filter => filter.name === newValue);
			const selectedSensorType = selectedApplication.sensorTypes[0];
			const selectedOptimization = selectedSensorType.optimizations[0];
			const updatedFilters = {
				application: newValue,
				sensorType: selectedSensorType.name,
				optimization: selectedOptimization.name,
				solutionId: selectedOptimization.solutionId,
			};
			dispatch({ type: SET_SELECTED_FILTERS, payload: updatedFilters });

			sendUserInteractionsToTelemetryFor(
				APPLICATION_SELECTED_EVENT,
				{ selection: newValue },
				updatedFilters.solutionId,
			);
		},
		[dispatch],
	);

	/**
	 * Selects first value for 'optimization', updates 'selectedFilters' global state and url params
	 * @param {string} newValue Updated value of 'sensorType' filter dropdown
	 */
	const onSensorTypeFilterChange = useCallback(
		newValue => {
			const selectedSensorType = selectedApplication.sensorTypes.find(
				filter => filter.name === newValue,
			);
			const newOptimization = selectedSensorType.optimizations[0];
			const updatedFilters = {
				application,
				sensorType: selectedSensorType.name,
				optimization: newOptimization.name,
				solutionId: newOptimization.solutionId,
			};
			dispatch({ type: SET_SELECTED_FILTERS, payload: updatedFilters });

			sendUserInteractionsToTelemetryFor(
				SENSOR_SELECTED_EVENT,
				{ selection: newValue },
				updatedFilters.solutionId,
			);
		},
		[application, selectedApplication, dispatch],
	);

	/**
	 * Updates 'selectedFilters' global state and url params
	 * @param {string} newValue Updated value of the 'optimization' filter dropdown
	 */
	const onOptimizationFilterChange = useCallback(
		newValue => {
			const selectedOptimization = selectedSensorType.optimizations.find(
				filter => filter.name === newValue,
			);
			const updatedFilters = {
				application,
				sensorType,
				optimization: selectedOptimization.name,
				solutionId: selectedOptimization.solutionId,
			};
			dispatch({ type: SET_SELECTED_FILTERS, payload: updatedFilters });

			sendUserInteractionsToTelemetryFor(
				OPTIMIZATION_SELECTED_EVENT,
				{ selection: newValue },
				updatedFilters.solutionId,
			);
		},
		[application, sensorType, selectedSensorType, dispatch],
	);

	// Update to default filters if user enters invalid filter url params
	useEffect(() => {
		if (isAnyParamInvalid) dispatch({ type: SET_SELECTED_FILTERS, payload: DEFAULT_FILTERS });
	}, [isAnyParamInvalid, dispatch]);

	return (
		<section className={classes.join(' ')}>
			<header className={classes[0] + '-title'}>
				<h3>{filters}</h3>
			</header>

			<div className={classes[0] + '-dropdowns'}>
				<SelectDropdown
					variant="application"
					label={applicationFilter}
					onChange={onApplicationFilterChange}
					options={APPLICATION_OPTIONS}
					currentValue={application}
				/>

				<SelectDropdown
					variant="sensorType"
					label={sensorTypeFilter}
					onChange={onSensorTypeFilterChange}
					options={sensorTypeOptions}
					currentValue={sensorType}
				/>

				<SelectDropdown
					variant="optimization"
					label={optimizationFilter}
					onChange={onOptimizationFilterChange}
					options={optimizationOptions}
					currentValue={optimization}
				/>
			</div>
		</section>
	);
};

export default SolutionFilters;
